import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, EmptyState, Loader } from "../../../../../../ui";
import { Callout, Dialog } from "../../ui-pending";
import "./BookingFrequencyModal.scss";
import { FaCalendarAlt, FaCheck, FaTrash, FaRedo } from "react-icons/fa";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  previewSchedule,
  studentPaidClassSchedule,
  getMyClassesData,
} from "../../../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../../../utils/mixpanelTracking";
import {
  WEEK_COUNT_BY_OPTION,
  buildFrequencyOptions,
  buildPreviewSchedulePayload,
  formatClassLabel,
  formatSlotLabel,
  formatStudentTimezoneSlotLabel,
  formatSuccessSlotLine,
  getDefaultFrequencyOptionId,
  getNextClassNumber,
  getSlotsForWeeks,
  getTimezoneLabel,
  isBookEverythingOption,
  mapPreviewScheduleDates,
  mapRecurringSlots,
  shouldSkipFrequency,
  sortRawSlots,
} from "./bookingFrequencyUtils";

dayjs.extend(customParseFormat);

export type CanonicalBookingSlot = {
  slotDateInTimeZone: string;
  slotStartTimeInTimeZone: string;
  slotEndTimeInTimeZone?: string;
  teacherSlotInfoId: string | number;
  classNumber?: number | string;
  studentDateInTimeZone?: string;
  studentStartTimeInTimeZone?: string;
  studentEndTimeInTimeZone?: string;
};

type BookingContext = {
  studentId: string | number;
  studentUserId: string | number;
  teacherUserId: string | number;
  courseId: string | number;
  timezone?: string;
  studentName?: string;
};

type BookingFrequencyModalProps = {
  open: boolean;
  selectedSlots?: CanonicalBookingSlot[];
  bookingContext: BookingContext;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

type WeekOptionId = keyof typeof WEEK_COUNT_BY_OPTION;

const toCanonicalSlots = (slots: unknown): CanonicalBookingSlot[] =>
  (Array.isArray(slots) ? slots : []).filter(
    (slot): slot is CanonicalBookingSlot =>
      Boolean(slot) &&
      typeof (slot as CanonicalBookingSlot).slotDateInTimeZone === "string" &&
      typeof (slot as CanonicalBookingSlot).slotStartTimeInTimeZone === "string" &&
      (slot as CanonicalBookingSlot).teacherSlotInfoId != null
  );

const HIGHLIGHT_ROTATE_INTERVAL = 3000;

const RotatingHighlight = ({ items = [] as string[] }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, HIGHLIGHT_ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [items.length]);

  if (!items.length) return null;

  return (
    <p key={index} className="booking-option__highlight">
      {items[index]}
    </p>
  );
};

const BookingFrequencyModal: React.FC<BookingFrequencyModalProps> = ({
  open,
  selectedSlots = [],
  bookingContext,
  onClose,
  onSuccess,
}) => {
  const [selectedOption, setSelectedOption] = useState("all");
  const [step, setStep] = useState<"frequency" | "confirmation" | "success">("frequency");
  const [confirmationSlots, setConfirmationSlots] = useState<CanonicalBookingSlot[]>([]);
  const [recurringSlotsData, setRecurringSlotsData] = useState<any[]>([]);
  const [previewRemainingClasses, setPreviewRemainingClasses] = useState<number | null>(null);
  const [previewTotalClasses, setPreviewTotalClasses] = useState<number | null>(null);
  const [previewTimezone, setPreviewTimezone] = useState<string | null>(null);
  const [previewStudentTimezone, setPreviewStudentTimezone] = useState<string | null>(null);
  const [isRecurringLoading, setIsRecurringLoading] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [nextClassNumber, setNextClassNumber] = useState(0);
  const hasTrackedAutoSelectRef = useRef(false);

  // Prefer timezone from preview-schedule API when available (e.g. "GMT" / "IST")
  const timezoneLabel = getTimezoneLabel(
    previewTimezone || bookingContext?.timezone
  );
  // Student time label: use studentTimezone from preview-schedule as returned
  const studentTimezoneLabel =
    (previewStudentTimezone && previewStudentTimezone.trim()) ||
    getTimezoneLabel(bookingContext?.timezone);

  // Enrich calendar-selected slots with student-local times from preview-schedule
  const slotsForDisplay = useMemo(() => {
    if (!selectedSlots?.length) return [];
    if (!recurringSlotsData?.length) return selectedSlots;

    const previewCanonical = toCanonicalSlots(
      mapRecurringSlots(recurringSlotsData)
    );

    return selectedSlots.map((slot) => {
      const exact = previewCanonical.find(
        (p) =>
          p.slotDateInTimeZone === slot.slotDateInTimeZone &&
          p.slotStartTimeInTimeZone === slot.slotStartTimeInTimeZone
      );
      const byTime = exact
        ? null
        : previewCanonical.find(
            (p) => p.slotStartTimeInTimeZone === slot.slotStartTimeInTimeZone
          );
      const match = exact || byTime;
      if (!match?.studentDateInTimeZone || !match?.studentStartTimeInTimeZone) {
        return slot;
      }
      return {
        ...slot,
        studentDateInTimeZone: match.studentDateInTimeZone,
        studentStartTimeInTimeZone: match.studentStartTimeInTimeZone,
        studentEndTimeInTimeZone: match.studentEndTimeInTimeZone,
      };
    });
  }, [selectedSlots, recurringSlotsData]);

  useEffect(() => {
    if (!open) {
      setStep("frequency");
      setConfirmationSlots([]);
      setSelectedOption("all");
      setRecurringSlotsData([]);
      setPreviewRemainingClasses(null);
      setPreviewTotalClasses(null);
      setPreviewTimezone(null);
      setPreviewStudentTimezone(null);
      setIsRecurringLoading(false);
      setIsBooking(false);
      setNextClassNumber(0);
      hasTrackedAutoSelectRef.current = false;
      return;
    }

    if (shouldSkipFrequency(selectedSlots)) {
      setConfirmationSlots([...selectedSlots]);
      setStep("confirmation");
      setIsRecurringLoading(false);
      setRecurringSlotsData([]);
      setPreviewRemainingClasses(null);
      setPreviewTotalClasses(null);
      setPreviewTimezone(null);
      setPreviewStudentTimezone(null);
    } else {
      setConfirmationSlots([]);
      setStep("frequency");
      setIsRecurringLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || step !== "frequency" || shouldSkipFrequency(selectedSlots)) return undefined;

    let isActive = true;

    async function fetchRecurringSlots() {
      try {
        setIsRecurringLoading(true);
        setRecurringSlotsData([]);
        setPreviewRemainingClasses(null);
        setPreviewTotalClasses(null);
        setPreviewTimezone(null);
        setPreviewStudentTimezone(null);

        const payload = buildPreviewSchedulePayload(selectedSlots, {
          studentId: bookingContext.studentId,
          teacherUserId: bookingContext.teacherUserId,
          courseId: bookingContext.courseId,
        });

        if (
          !payload?.studentId ||
          !payload?.teacherUserId ||
          !payload?.courseId ||
          !payload?.matchedSlots?.length
        ) {
          if (isActive) setIsRecurringLoading(false);
          toast.error("Missing student, teacher, or course details for booking preview.");
          return;
        }

        const data = await previewSchedule(payload);
        if (!isActive) return;

        const previewSlots = mapPreviewScheduleDates(
          Array.isArray(data?.dates) ? data.dates : []
        );
        const sortedData = previewSlots.length ? sortRawSlots(previewSlots) : [];

        const remainingCount = Number(data?.remainingClasses);
        const nextRecurringSlots =
          Number.isFinite(remainingCount) &&
          remainingCount > 0 &&
          sortedData.length > remainingCount
            ? sortedData.slice(0, remainingCount)
            : sortedData;

        const totalCount = Number(data?.totalClasses);
        setPreviewRemainingClasses(Number.isFinite(remainingCount) ? remainingCount : null);
        setPreviewTotalClasses(Number.isFinite(totalCount) ? totalCount : null);
        setPreviewTimezone(
          data?.timezone != null && String(data.timezone).trim() !== ""
            ? String(data.timezone).trim()
            : null
        );
        setPreviewStudentTimezone(
          data?.studentTimezone != null && String(data.studentTimezone).trim() !== ""
            ? String(data.studentTimezone).trim()
            : null
        );
        setRecurringSlotsData(nextRecurringSlots);

        if (remainingCount === 1) {
          const mapped = toCanonicalSlots(mapRecurringSlots(nextRecurringSlots));
          setConfirmationSlots(mapped.length ? mapped : [...selectedSlots]);
          setStep("confirmation");
        }
      } catch (error) {
        console.error("BookingFrequency recurringSlots error", error);
        toast.error("Failed to load recurring slots. Please try again.");
      } finally {
        if (isActive) setIsRecurringLoading(false);
      }
    }

    fetchRecurringSlots();

    return () => {
      isActive = false;
    };
  }, [open, step, bookingContext, selectedSlots]);

  useEffect(() => {
    if (!open) return undefined;

    const { studentUserId, courseId } = bookingContext;
    if (!studentUserId || !courseId) return undefined;

    let isActive = true;

    async function fetchNextClassNumber() {
      try {
        const data = await getMyClassesData(studentUserId, courseId);
        if (!isActive) return;
        setNextClassNumber(getNextClassNumber(Array.isArray(data) ? data : []));
      } catch (error) {
        console.error("BookingFrequency myClasses error", error);
      }
    }

    fetchNextClassNumber();

    return () => {
      isActive = false;
    };
  }, [open, bookingContext.studentUserId, bookingContext.courseId]);

  const frequencyOptions = useMemo(
    () => buildFrequencyOptions(recurringSlotsData, previewRemainingClasses),
    [recurringSlotsData, previewRemainingClasses]
  );

  const trackFrequencySelection = (optionId: string) => {
    const option = frequencyOptions.find((item: any) => item.id === optionId);
    trackEvent(MixpanelEvents.BOOKING_FREQUENCY_SELECTED, {
      page: "Admin Student Calendar Booking Frequency",
      optionId,
      slotsSelectedCount: option?.slotCount ?? selectedSlots.length,
      packRemaining: previewRemainingClasses,
      classCount: previewTotalClasses,
      isDefaultSelection: optionId === getDefaultFrequencyOptionId(frequencyOptions),
      scheduleType: optionId === "all" ? "fixed" : "flexible",
    });
  };

  useEffect(() => {
    if (!open || isRecurringLoading || !frequencyOptions.length) return;
    if (step !== "frequency") return;
    if (shouldSkipFrequency(selectedSlots) || Number(previewRemainingClasses) === 1) return;

    const nextOptionId = frequencyOptions.some((option: any) => option.id === selectedOption)
      ? selectedOption
      : getDefaultFrequencyOptionId(frequencyOptions);

    if (nextOptionId !== selectedOption) {
      setSelectedOption(nextOptionId);
    }

    if (!hasTrackedAutoSelectRef.current) {
      hasTrackedAutoSelectRef.current = true;
      trackEvent(MixpanelEvents.BOOKING_FREQUENCY_LANDED, {
        page: "Admin Student Calendar Booking Frequency",
        Source: "Admin calendar",
      });
      trackFrequencySelection(nextOptionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, frequencyOptions, selectedOption, isRecurringLoading, step, previewRemainingClasses]);

  const handleSelectOption = (optionId: string) => {
    setSelectedOption(optionId);
    trackFrequencySelection(optionId);
  };

  const handleContinue = () => {
    const weekCount = WEEK_COUNT_BY_OPTION[selectedOption as WeekOptionId];
    if (isBookEverythingOption(selectedOption) && recurringSlotsData.length) {
      setConfirmationSlots(toCanonicalSlots(mapRecurringSlots(recurringSlotsData)));
    } else if (weekCount) {
      const weekSlots = getSlotsForWeeks(recurringSlotsData, weekCount);
      setConfirmationSlots(
        weekSlots.length
          ? toCanonicalSlots(mapRecurringSlots(weekSlots))
          : [...selectedSlots]
      );
    } else {
      setConfirmationSlots([...selectedSlots]);
    }
    setStep("confirmation");
  };

  const handleRemoveConfirmationSlot = (index: number) => {
    setConfirmationSlots((prev) => {
      const updated = prev.filter((_, slotIndex) => slotIndex !== index);
      if (updated.length === 0) onClose();
      return updated;
    });
  };

  const skipFrequency =
    shouldSkipFrequency(selectedSlots) || Number(previewRemainingClasses) === 1;
  const slotsForConfirm = confirmationSlots.length ? confirmationSlots : selectedSlots;
  const showFrequencyStep = step === "frequency" && !skipFrequency;

  const handleConfirmBook = async () => {
    if (isBooking) return;

    const payload = {
      studentUserId: Number(bookingContext.studentUserId),
      courseId: String(bookingContext.courseId ?? ""),
      selectedSlots: slotsForConfirm
        .map((slot) => ({
          teacherSlotInfoId: String(slot?.teacherSlotInfoId ?? ""),
        }))
        .filter((slot) => slot.teacherSlotInfoId),
      scheduleType: !skipFrequency && selectedOption === "all" ? "fixed" : "flexible",
    };

    if (!payload.selectedSlots.length) {
      toast.error("No slots available to book.");
      return;
    }

    try {
      setIsBooking(true);
      await studentPaidClassSchedule(payload);

      trackEvent(MixpanelEvents.BOOKING_FREQUENCY_SUCCESS, {
        page: "Admin Student Calendar Booking Frequency",
        source: "admin calendar",
        all_classes_booked: !skipFrequency && selectedOption === "all",
        classes_booked_count: payload.selectedSlots.length,
        scheduleType: payload.scheduleType,
        studentId: bookingContext.studentId,
        studentUserId: bookingContext.studentUserId,
        courseId: bookingContext.courseId,
        teacherUserId: bookingContext.teacherUserId,
      });

      trackEvent(MixpanelEvents.SCHEDULE_CLASS, {
        page: "Admin Student Calendar Booking Frequency",
        studentUserId: bookingContext.studentUserId,
        courseId: bookingContext.courseId,
        teacherUserId: bookingContext.teacherUserId,
        selectedSlots: payload.selectedSlots,
        scheduleType: payload.scheduleType,
      });

      if (onSuccess) await onSuccess();
      setStep("success");
      toast.success(
        `${payload.selectedSlots.length} ${
          payload.selectedSlots.length === 1 ? "class" : "classes"
        } booked for the student`
      );
    } catch (error) {
      console.error("BookingFrequency schedule error", error);
      toast.error("Failed to book classes. Please try again.");
    } finally {
      setIsBooking(false);
    }
  };

  const renderFrequencyStep = () => {
    if (isRecurringLoading) {
      return <Loader label="Loading available weeks…" />;
    }

    return (
      <>
        <section className="booking-frequency__section">
          <h3 className="booking-frequency__heading">
            {slotsForDisplay.length > 1 ? "Selected Slots" : "Selected Slot"}
          </h3>
          <div className="booking-frequency__list">
            {slotsForDisplay.length ? (
              slotsForDisplay.map((slot, index) => {
                const studentTimeLine = formatStudentTimezoneSlotLabel(
                  slot,
                  studentTimezoneLabel
                );
                return (
                <div
                  key={`${slot.slotDateInTimeZone}-${slot.slotStartTimeInTimeZone}-${index}`}
                  className="booking-slot-card"
                >
                  <FaCalendarAlt aria-hidden />
                  <div className="booking-slot-card__body">
                    <span className="booking-slot-card__title">
                      {formatSlotLabel(slot, timezoneLabel)} {timezoneLabel}
                    </span>
                    {studentTimeLine ? (
                      <p className="booking-slot-card__meta">
                        Student time: {studentTimeLine}
                      </p>
                    ) : null}
                  </div>
                </div>
                );
              })
            ) : (
              <EmptyState title="No slots selected." description="Close this dialog and pick a free slot on the calendar." />
            )}
          </div>
        </section>

        <section className="booking-frequency__section">
          <div className="booking-frequency__question">
            <h3 className="booking-frequency__heading">
              Repeat this slot for the student over multiple weeks?
            </h3>
            <span className="booking-frequency__icon" aria-hidden>
              <FaRedo size={14} />
            </span>
          </div>

          <div className="booking-frequency__list" role="group" aria-label="Booking frequency">
            {frequencyOptions.length === 0 ? (
              <Callout tone="info">
                No recurring slots found for this pattern. You can still book the selected slot
                from confirmation.
              </Callout>
            ) : (
              frequencyOptions.map((option: any) => {
                const isSelected = selectedOption === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => handleSelectOption(option.id)}
                    className={`booking-option${isSelected ? " booking-option--selected" : ""}`}
                  >
                    {option.showBestForYou && (
                      <span className="booking-option__ribbon">
                        <Badge tone="success" solid>Recommended</Badge>
                      </span>
                    )}
                    <div className="booking-option__row">
                      <div className="booking-option__body">
                        <p className="booking-option__title">
                          {option.title}
                        </p>
                        {option.subtitle ? (
                          <p className="booking-option__subtitle">
                            {option.subtitle}
                          </p>
                        ) : null}
                        {option.highlights?.length ? (
                          <RotatingHighlight items={option.highlights} />
                        ) : null}
                        {option.classes ? (
                          <p className="booking-option__meta">
                            {option.classes}
                          </p>
                        ) : null}
                      </div>
                      {isSelected ? (
                        <span className="booking-option__check" aria-hidden>
                          <FaCheck size={11} />
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>
      </>
    );
  };

  const renderConfirmationStep = () => {
    const slotCount = slotsForConfirm.length;
    const classWord = slotCount === 1 ? "class" : "classes";
    const studentLabel = bookingContext.studentName
      ? bookingContext.studentName
      : "this student";

    return (
      <>
        <div className="booking-frequency__hero">
          <FaCalendarAlt size={36} aria-hidden />
          <p className="booking-frequency__hero-title">
            Book the next {slotCount} {classWord} for {studentLabel}
          </p>
        </div>

        <div className="booking-frequency__list">
          {slotsForConfirm.map((slot, index) => {
            const studentTimeLine = formatStudentTimezoneSlotLabel(
              slot,
              studentTimezoneLabel
            );
            return (
            <div
              key={`${slot.slotDateInTimeZone}-${slot.slotStartTimeInTimeZone}-${index}`}
              className="booking-slot-card"
            >
              <FaCalendarAlt aria-hidden />
              <div className="booking-slot-card__body">
                <p className="booking-slot-card__title">
                  {formatClassLabel(slot, index, nextClassNumber)}
                </p>
                <p className="booking-slot-card__line">
                  {formatSlotLabel(slot, timezoneLabel)} {timezoneLabel}
                </p>
                {studentTimeLine ? (
                  <p className="booking-slot-card__meta">
                    Student time: {studentTimeLine}
                  </p>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveConfirmationSlot(index)}
                disabled={isBooking}
                aria-label="Remove slot"
                icon={<FaTrash aria-hidden />}
              />
            </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderSuccessStep = () => {
    const slotCount = slotsForConfirm.length;
    const classWord = slotCount === 1 ? "class" : "classes";
    const slotSummary = formatSuccessSlotLine(slotsForConfirm, timezoneLabel);
    const studentLabel = bookingContext.studentName
      ? bookingContext.studentName
      : "the student";

    return (
      <div className="booking-frequency__hero">
        <span className="booking-frequency__icon booking-frequency__icon--lg" aria-hidden>
          <FaCheck size={28} />
        </span>
        <p className="booking-frequency__success-title">
          {slotCount} {classWord} booked for {studentLabel}
        </p>
        {slotSummary ? (
          <div className="booking-frequency__summary">
            <FaCalendarAlt aria-hidden />
            <span>{slotSummary}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const dialogTitle =
    step !== "success" && showFrequencyStep
      ? "Book Classes for Student"
      : step === "confirmation"
      ? "Confirm Student Booking"
      : step === "success"
      ? "Booking Complete"
      : null;

  return (
    <Dialog
      isOpen={open}
      onClose={() => !isBooking && onClose()}
      className="booking-frequency-dialog"
      title={dialogTitle}
      actions={
        step !== "success" ? (
          showFrequencyStep ? (
            <Button
              variant="primary"
              size="lg"
              block
              disabled={!slotsForDisplay.length || isRecurringLoading}
              onClick={() => {
                if (!frequencyOptions.length) {
                  setConfirmationSlots([...selectedSlots]);
                  setStep("confirmation");
                  return;
                }
                handleContinue();
              }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              block
              disabled={!slotsForConfirm.length || isBooking}
              loading={isBooking}
              onClick={handleConfirmBook}
            >
              {isBooking ? "Loading..." : "Confirm & Book for Student"}
            </Button>
          )
        ) : (
          <Button variant="primary" size="lg" block onClick={onClose}>
            Done
          </Button>
        )
      }
    >
        {step === "success"
          ? renderSuccessStep()
          : showFrequencyStep
          ? renderFrequencyStep()
          : renderConfirmationStep()}
    </Dialog>
  );
};

export default BookingFrequencyModal;

/** Map admin calendar free-slot eventDetails → canonical booking slot */
export const mapEventDetailsToCanonicalSlot = (
  eventDetails: any
): CanonicalBookingSlot | null => {
  if (!eventDetails) return null;

  const slotDate = String(
    eventDetails.slotDate || eventDetails.slotDateInTimeZone || eventDetails.classDate || ""
  ).split("T")[0];
  const startRaw = String(
    eventDetails.startTime ||
      eventDetails.slotStartTimeInTimeZone ||
      eventDetails.classStartTime ||
      ""
  );
  const endRaw = String(
    eventDetails.endTime ||
      eventDetails.slotEndTimeInTimeZone ||
      eventDetails.classEndTime ||
      ""
  );
  const slotId =
    eventDetails.slotId ??
    eventDetails.teacherSlotInfoId ??
    eventDetails.teacherSlotId;

  if (!slotDate || !startRaw || slotId == null || String(slotId).trim() === "") {
    return null;
  }

  const startParts = startRaw.split(":");
  const endParts = endRaw ? endRaw.split(":") : [];
  const startHHMM = `${startParts[0]?.padStart(2, "0")}:${(startParts[1] || "00").padStart(2, "0")}`;
  const endHHMM = endParts.length
    ? `${endParts[0]?.padStart(2, "0")}:${(endParts[1] || "00").padStart(2, "0")}`
    : undefined;

  const date = dayjs(slotDate, "YYYY-MM-DD");
  if (!date.isValid()) return null;

  return {
    slotDateInTimeZone: date.format("DD/MM/YYYY"),
    slotStartTimeInTimeZone: startHHMM,
    slotEndTimeInTimeZone: endHHMM,
    teacherSlotInfoId: slotId,
  };
};
